package com.gsb.vyapar.bhagya.datasource.network

import com.gsb.vyapar.bhagya.datasource.network.request.AuthRequest
import com.gsb.vyapar.bhagya.datasource.network.request.GitHubUploadBody
import com.gsb.vyapar.bhagya.datasource.network.response.AuthResponse
import com.gsb.vyapar.bhagya.datasource.network.response.GithubUploadResponse
import com.gsb.vyapar.bhagya.datasource.network.response.ImageUploadResponse
import com.gsb.vyapar.bhagya.datasource.network.response.NominatimResponse
import com.gsb.vyapar.bhagya.datasource.network.response.RefreshAuthResponse
import com.gsb.vyapar.bhagya.datasource.runtimecache.RuntimeCache
import com.gsb.vyapar.bhagya.domain.*
import com.gsb.vyapar.bhagya.interactor.util.showError
import com.gsb.vyapar.bhagya.util.BuildConfig
import com.gsb.vyapar.bhagya.util.PlatformType
import com.gsb.vyapar.bhagya.util.gvbLog
import com.gsb.vyapar.bhagya.util.value
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.HttpSend
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.plugins.logging.DEFAULT
import io.ktor.client.plugins.logging.EMPTY
import io.ktor.client.plugins.logging.LogLevel
import io.ktor.client.plugins.logging.Logger
import io.ktor.client.plugins.logging.Logging
import io.ktor.client.plugins.plugin
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.delete
import io.ktor.client.request.forms.formData
import io.ktor.client.request.forms.submitForm
import io.ktor.client.request.forms.submitFormWithBinaryData
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.Headers
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpMethod
import io.ktor.http.HttpStatusCode
import io.ktor.http.Parameters
import io.ktor.serialization.JsonConvertException
import io.ktor.serialization.kotlinx.json.json
import io.ktor.util.encodeBase64
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.Serializable
import kotlinx.serialization.SerializationException
import kotlinx.serialization.json.Json
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

object KtorClient {
    private val mutex = Mutex()
    private val defaultClient: HttpClient = HttpClient {
        install(ContentNegotiation) {
            json(Json { isLenient = true; ignoreUnknownKeys = true; explicitNulls = false })
        }
        install(Logging) {
            logger = if (BuildConfig.platform() == PlatformType.ANDROID) {
                object : Logger {
                    override fun log(message: String) {
                        if (BuildConfig.isDebug()) gvbLog(message)
                    }
                }
            } else {
                if (BuildConfig.isDebug()) Logger.DEFAULT else Logger.EMPTY
            }
            level = if (BuildConfig.isDebug()) LogLevel.ALL else LogLevel.NONE
        }
    }
    val client: HttpClient = HttpClient {
        install(ContentNegotiation) {
            json(Json { isLenient = true; ignoreUnknownKeys = true; explicitNulls = false })
        }
        install(Logging) {
            logger = if (BuildConfig.platform() == PlatformType.ANDROID) {
                object : Logger {
                    override fun log(message: String) {
                        if (BuildConfig.isDebug()) gvbLog(message)
                    }
                }
            } else {
                if (BuildConfig.isDebug()) Logger.DEFAULT else Logger.EMPTY
            }
            level = if (BuildConfig.isDebug()) LogLevel.ALL else LogLevel.NONE
        }
    }

    init {
        client.plugin(HttpSend).intercept { originalRequest ->
            val response = execute(originalRequest)
            if (response.response.status == HttpStatusCode.Unauthorized) {
                mutex.withLock {
                    val result = refreshToken()
                    if (result.isSuccess) {
                        val refreshAuthResponse = result.getOrNull()
                        RuntimeCache.setTokens(refreshAuthResponse?.id_token.orEmpty(), refreshAuthResponse?.refresh_token.orEmpty())
                        originalRequest.url.parameters["auth"] = RuntimeCache.accessToken
                        return@intercept execute(originalRequest)
                    } else {
                        val loginResult = anonymousLogin()
                        if (loginResult.isSuccess) {
                            val authResponse = loginResult.getOrNull()
                            RuntimeCache.setTokens(authResponse?.idToken.orEmpty(), authResponse?.refreshToken.orEmpty())
                            originalRequest.url.parameters["auth"] = RuntimeCache.accessToken
                            return@intercept execute(originalRequest)
                        } else {
                            showError("Please relaunch the App")
                            response
                        }
                    }
                }
            } else {
                response
            }
        }
    }

    private suspend fun refreshToken(): Result<RefreshAuthResponse> {
        return try {
            handleHttpResponse(
                defaultClient.submitForm(
                    "${REFRESH_TOKEN_BASEURL.value()}${API_KEY.value()}",
                    formParameters = Parameters.build {
                        append("grant_type", "refresh_token")
                        append("refresh_token", RuntimeCache.refreshToken)
                    })
            )
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    @Serializable
    data class ErrorResponse(val errorCode: Int, val message: String)

    private suspend inline fun <reified T> makeApiRequest(
        url: String,
        httpMethod: HttpMethod,
        headers: Map<String, String> = emptyMap(),
        requestBody: Any? = null
    ): Result<T> {
        return try {
            val response: HttpResponse = when (httpMethod) {
                HttpMethod.Get -> client.get(url) {
                    applyHeaders(headers)
                }

                HttpMethod.Post -> client.post(url) {
                    applyHeaders(headers)
                    requestBody?.let { setBody(it) }
                }

                HttpMethod.Put -> client.put(url) {
                    applyHeaders(headers)
                    requestBody?.let { setBody(it) }
                }

                HttpMethod.Delete -> client.delete(url) {
                    applyHeaders(headers)
                    requestBody?.let { setBody(it) }
                }

                HttpMethod.Patch -> client.patch(url) {
                    applyHeaders(headers)
                    requestBody?.let { setBody(it) }
                }

                else -> throw IllegalArgumentException("Unsupported HTTP method")
            }
            handleHttpResponse(response)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    private fun HttpRequestBuilder.applyHeaders(headers: Map<String, String>) {
        headers.forEach { (key, value) ->
            header(key, value)
        }
    }

    suspend fun uploadImage(data: ByteArray, fileName: String): Result<ImageUploadResponse> {
        return try {
            handleHttpResponse(
                defaultClient.submitFormWithBinaryData(
                    url = IMAGE_UPLOAD_URL.value(),
                    formData = formData {
                        append("file", data, Headers.build {
                            append(HttpHeaders.ContentType, "image/*")
                            append(HttpHeaders.ContentDisposition, "filename=${fileName}")
                        })
                        append("api_key", IMAGE_UPLOAD_KEY.value())
                    })
            )
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    @OptIn(ExperimentalTime::class)
    suspend fun uploadGithubImage(data: ByteArray): Result<GithubUploadResponse> {
        return makeApiRequest(GITHUB_UPLOAD_URL.value().replace(TIMESTAMP_URL.value(), Clock.System.now().epochSeconds.toString()), HttpMethod.Put, mapOf(headerJsonContentType, headerGithubAuthorization), GitHubUploadBody("main", data.encodeBase64(), "Image Upload"))
    }

    @OptIn(ExperimentalTime::class)
    suspend fun uploadGithubImageN(data: ByteArray): Result<GithubUploadResponse> {
        return makeApiRequest(GITHUB_UPLOAD_URL_N.value().replace(TIMESTAMP_URL.value(), Clock.System.now().epochSeconds.toString()), HttpMethod.Put, mapOf(headerJsonContentType, headerGithubAuthorizationN), GitHubUploadBody("main", data.encodeBase64(), "Image Upload"))
    }

    suspend fun downloadImage(url: String): Result<ByteArray?> {
        return try {
            handleHttpResponse(defaultClient.get(url))
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    suspend fun getLocation(pincode: String): Result<Location> {
        return try {
            handleHttpResponse(defaultClient.get(LOCATION_URL.value().replace(PINCODE_URL.value(), pincode)))
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    private suspend inline fun <reified T : Any> handleHttpResponse(result: HttpResponse): Result<T> {
        return when (result.status) {
            HttpStatusCode.OK, HttpStatusCode.Created, HttpStatusCode.Accepted -> {
                try {
                    val responseBody: T = result.body()
                    Result.success(responseBody)
                } catch (e: Exception) {
                    when (e) {
                        is JsonConvertException -> Result.failure(Exception("No data available"))
                        else -> Result.failure(e)
                    }
                }
            }
            else -> {
                val errorBody = result.bodyAsText()
                val errorResponse: ErrorResponse? = try {
                    Json.decodeFromString<ErrorResponse>(errorBody)
                } catch (e: SerializationException) {
                    null
                }
                Result.failure(Exception("Error: ${errorResponse?.message ?: errorBody}"))
            }
        }
    }

    private suspend fun updateDto(url: String, dto: Dto): Result<Unit> {
        return makeApiRequest(url, HttpMethod.Put, mapOf(headerJsonContentType), dto)
    }

    private suspend fun updateDtoId(url: String, dtoId: DtoId): Result<Unit> {
        return makeApiRequest(url, HttpMethod.Put, mapOf(headerJsonContentType), dtoId)
    }

    suspend fun anonymousLogin(): Result<AuthResponse> {
        return handleHttpResponse(defaultClient.post("${REGISTER_BASEURL.value()}${API_KEY.value()}") {
            applyHeaders(mapOf(headerJsonContentType))
            setBody(AuthRequest(returnSecureToken = true))
        })
    }

    suspend fun getCategories(): Result<Dto> {
        return makeApiRequest(CATEGORIES_URL.value() + RuntimeCache.accessToken, HttpMethod.Get)
    }

    suspend fun updateCategories(categories: Categories): Result<Unit> {
        return updateDto(CATEGORIES_URL.value() + RuntimeCache.accessToken, categories.toDto())
    }

    suspend fun getSubCategories(): Result<Dto> {
        return makeApiRequest(SUB_CATEGORIES_URL.value() + RuntimeCache.accessToken, HttpMethod.Get)
    }

    suspend fun updateSubCategories(subCategories: SubCategories): Result<Unit> {
        return updateDto(SUB_CATEGORIES_URL.value() + RuntimeCache.accessToken, subCategories.toDto())
    }

    suspend fun getInfo(): Result<Dto> {
        return makeApiRequest(INFO_URL.value() + RuntimeCache.accessToken, HttpMethod.Get)
    }

    suspend fun updateInfo(info: Info): Result<Unit> {
        return updateDto(INFO_URL.value() + RuntimeCache.accessToken, info.toDto())
    }

    suspend fun getConfig(): Result<Dto> {
        return makeApiRequest(CONFIG_URL.value() + RuntimeCache.accessToken, HttpMethod.Get)
    }

    suspend fun getConfigVersion(): Result<Int> {
        return makeApiRequest(CONFIG_V_URL.value() + RuntimeCache.accessToken, HttpMethod.Get)
    }

    suspend fun updateConfig(config: Config): Result<Unit> {
        return updateDto(CONFIG_URL.value() + RuntimeCache.accessToken, config.toDto())
    }

    suspend fun getUser(userId: String): Result<Dto> {
        return makeApiRequest(USERS_URL.value().replace(USER_ID.value(), userId) + RuntimeCache.accessToken, HttpMethod.Get)
    }

    suspend fun deleteUser(user: User): Result<Unit> {
        return makeApiRequest(USERS_URL.value().replace(USER_ID.value(), user.getId()) + RuntimeCache.accessToken, HttpMethod.Delete)
    }

    suspend fun getUserVersion(userId: String): Result<Int> {
        return makeApiRequest(USERS_VERSION_URL.value().replace(USER_ID.value(), userId) + RuntimeCache.accessToken, HttpMethod.Get)
    }

    suspend fun updateUser(user: User): Result<Unit> {
        return updateDto(USERS_URL.value().replace(USER_ID.value(), user.getId()) + RuntimeCache.accessToken, user.toDto())
    }

    suspend fun updateMBusiness(business: Business): Result<Unit> {
        return updateDtoId(M_BUSINESS_URL.value().replace(USER_ID.value(), business.getUserId()).replace(TEMP_ID.value(), business.getTempId()) + RuntimeCache.accessToken, business.toDtoId())
    }

    suspend fun deleteMBusiness(business: Business): Result<Unit> {
        return makeApiRequest(M_BUSINESS_URL.value().replace(USER_ID.value(), business.getUserId()).replace(TEMP_ID.value(), business.getTempId()) + RuntimeCache.accessToken + RuntimeCache.accessToken, HttpMethod.Delete)
    }

    suspend fun getMBusinessUser(user: User): Result<Map<String, DtoId>> {
        return makeApiRequest(M_BUSINESS_USER_URL.value().replace(USER_ID.value(), user.getId()) + RuntimeCache.accessToken, HttpMethod.Get)
    }

    suspend fun getMBusinessAll(): Result<Map<String, Map<String, DtoId>>> {
        return makeApiRequest(M_BUSINESS_ALL_URL.value() + RuntimeCache.accessToken, HttpMethod.Get)
    }

    suspend fun updateBusiness(business: Business): Result<Unit> {
        return updateDtoId(BUSINESS_URL.value().replace(BUSINESS_ID.value(), business.getId()) + RuntimeCache.accessToken, business.toDtoId())
    }

    suspend fun getBusiness(id: String): Result<DtoId> {
        return makeApiRequest(BUSINESS_URL.value().replace(BUSINESS_ID.value(), id) + RuntimeCache.accessToken, HttpMethod.Get)
    }

    suspend fun getBusinessVersion(id: String): Result<Int> {
        return makeApiRequest(BUSINESS_VERSION_URL.value().replace(BUSINESS_ID.value(), id) + RuntimeCache.accessToken, HttpMethod.Get)
    }

    suspend fun deleteBusiness(business: Business): Result<Unit> {
        return makeApiRequest(BUSINESS_URL.value().replace(BUSINESS_ID.value(), business.getId()) + RuntimeCache.accessToken, HttpMethod.Delete)
    }

    suspend fun getBusinessAll(): Result<Map<String, DtoId>> {
        return makeApiRequest(BUSINESS_ALL_URL.value() + RuntimeCache.accessToken, HttpMethod.Get)
    }

    suspend fun getBusinessIndexAll(index: Int): Result<Map<String, DtoId>> {
        return makeApiRequest(BUSINESS_INDEX_ALL_URL.value().replace(INDEX.value(), index.toString()) + RuntimeCache.accessToken, HttpMethod.Get)
    }

    suspend fun getNominatimLocation(latitude: String, longitude: String): Result<NominatimResponse> {
        return makeApiRequest(NOMINATIM_URL.value().replace(LATITUDE_URL.value(), latitude).replace(LONGITUDE_URL.value(), longitude), HttpMethod.Get)
    }
}